import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../../shared/services/toast.service';

const IDLE_TIMEOUT_MESSAGE = 'انتهت جلستك بسبب عدم النشاط، يرجى تسجيل الدخول مرة أخرى';

/** Idle-timeout 401 envelope shape — distinct from the standard `IApiResponse` failure shape (see §2.3) */
interface IIdleTimeoutErrorBody {
    code?: string;
}

const attachToken = (req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> => {
    const headers = token
        ? req.headers.set('Authorization', `Bearer ${token}`)
        : req.headers;
    return req.clone({ headers, withCredentials: true });
};

const handle401 = (
    original: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService
) =>
    from(authService.refreshToken()).pipe(
        switchMap(succeeded => {
            if (succeeded) {
                return next(attachToken(original, authService.getAccessToken()));
            }
            return from(authService.logout()).pipe(
                switchMap(() => throwError(() => new HttpErrorResponse({ status: 401 })))
            );
        })
    );

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const toastService = inject(ToastService);

    const isAuthEndpoint =
        req.url.includes('/auth/refresh') ||
        req.url.includes('/auth/logout') ||
        req.url.includes('/auth/login') ||
        req.url.includes('/auth/admin/login');

    const outgoing = attachToken(req, authService.getAccessToken());

    return next(outgoing).pipe(
        catchError((err: HttpErrorResponse) => {
            if (err.status === 401) {
                const body = err.error as IIdleTimeoutErrorBody | null;
                if (body?.code === 'IDLE_TIMEOUT') {
                    toastService.warn(IDLE_TIMEOUT_MESSAGE);
                    return from(authService.logout()).pipe(
                        switchMap(() => throwError(() => err))
                    );
                }
                if (!isAuthEndpoint) {
                    return handle401(req, next, authService);
                }
            }
            return throwError(() => err);
        })
    );
};
